import sanitizeHtml from "sanitize-html"

type ExportElementToPdfOptions = {
  elementId: string
  fileName: string
  documentTitle?: string
  onBeforeExport?: (element: HTMLElement) => void
  onAfterExport?: (element: HTMLElement) => void
}

const sanitizePrintableHtml = (rawHtml: string) => {
  return sanitizeHtml(rawHtml, {
    allowedTags: [
      ...sanitizeHtml.defaults.allowedTags,
      "img",
      "table",
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "th",
      "td",
      "svg",
      "g",
      "path",
      "line",
      "polyline",
      "polygon",
      "circle",
      "rect",
      "ellipse",
      "text"
    ],
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      "*": ["class", "colspan", "rowspan", "aria-label", "role"],
      a: ["href", "target", "rel", "name"],
      img: ["src", "alt", "width", "height"],
      svg: ["viewBox", "width", "height", "xmlns", "fill", "stroke"],
      g: ["fill", "stroke", "transform"],
      path: ["d", "fill", "stroke", "stroke-width", "transform"],
      line: ["x1", "x2", "y1", "y2", "stroke", "stroke-width"],
      polyline: ["points", "fill", "stroke", "stroke-width"],
      polygon: ["points", "fill", "stroke", "stroke-width"],
      circle: ["cx", "cy", "r", "fill", "stroke", "stroke-width"],
      rect: [
        "x",
        "y",
        "width",
        "height",
        "rx",
        "ry",
        "fill",
        "stroke",
        "stroke-width"
      ],
      ellipse: ["cx", "cy", "rx", "ry", "fill", "stroke", "stroke-width"],
      text: ["x", "y", "fill", "font-size", "font-family", "text-anchor"]
    },
    allowedSchemes: ["http", "https", "mailto", "tel", "data"],
    allowedSchemesByTag: {
      img: ["http", "https", "data"]
    },
    disallowedTagsMode: "discard"
  })
}

const getDocumentStyles = () => {
  const styleNodes = Array.from(
    document.querySelectorAll("style, link[rel='stylesheet']")
  )
  return styleNodes.map((node) => node.outerHTML).join("\n")
}

const waitForImagesToLoad = (root: Document) => {
  const images = Array.from(root.images || [])
  if (images.length === 0) {
    return Promise.resolve()
  }

  return Promise.all(
    images.map((image) => {
      if (image.complete) {
        return Promise.resolve()
      }

      return new Promise<void>((resolve) => {
        image.addEventListener("load", () => resolve(), { once: true })
        image.addEventListener("error", () => resolve(), { once: true })
      })
    })
  ).then(() => undefined)
}

const buildPrintDocument = (html: string, title: string) => {
  const documentStyles = getDocumentStyles()
  const printStyles = `
        <style>
            @page { size: A4 portrait; margin: 16mm; }
            body { margin: 0; padding: 0; background: #ffffff; color: #000000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .pdf-export-root { padding: 0; }
            .no-print { display: none !important; }
        </style>
    `

  return `
        <!doctype html>
        <html>
        <head>
            <meta charset="utf-8" />
            <base href="${window.location.origin}/" />
            <title>${title}</title>
            ${documentStyles}
            ${printStyles}
        </head>
        <body>
            <div class="pdf-export-root">${html}</div>
        </body>
        </html>
    `
}

// Renders sanitized content into a hidden same-origin iframe and prints it.
// This intentionally avoids window.open(), which browsers/popup-blockers can
// silently block, leaving the user with no PDF and no error.
const printViaHiddenIframe = (html: string, title: string) => {
  return new Promise<boolean>((resolve) => {
    const iframe = document.createElement("iframe")
    iframe.style.position = "fixed"
    iframe.style.right = "0"
    iframe.style.bottom = "0"
    iframe.style.width = "0"
    iframe.style.height = "0"
    iframe.style.border = "0"
    iframe.setAttribute("aria-hidden", "true")

    const cleanup = () => {
      iframe.remove()
    }

    let settled = false
    const finish = (success: boolean) => {
      if (settled) return
      settled = true
      resolve(success)
    }

    iframe.onload = async () => {
      try {
        const iframeWindow = iframe.contentWindow
        const iframeDocument = iframe.contentDocument
        if (!iframeWindow || !iframeDocument) {
          finish(false)
          cleanup()
          return
        }

        await waitForImagesToLoad(iframeDocument)
        await new Promise<void>((r) =>
          iframeWindow.requestAnimationFrame(() => r())
        )

        iframeWindow.focus()
        iframeWindow.print()
        finish(true)
      } catch (e) {
        console.error("Unable to render print preview", e)
        finish(false)
      } finally {
        // Give the browser's print dialog time to read the iframe content
        // before it gets removed from the DOM.
        setTimeout(cleanup, 1000)
      }
    }

    document.body.appendChild(iframe)
    const iframeDocument = iframe.contentDocument
    if (!iframeDocument) {
      finish(false)
      cleanup()
      return
    }

    iframeDocument.open()
    iframeDocument.write(buildPrintDocument(html, title))
    iframeDocument.close()
  })
}

export const exportElementToPdf = async ({
  elementId,
  fileName,
  documentTitle,
  onBeforeExport,
  onAfterExport
}: ExportElementToPdfOptions): Promise<boolean> => {
  const targetElement = document.getElementById(elementId)
  if (!targetElement) {
    return false
  }

  onBeforeExport?.(targetElement)
  try {
    const clonedElement = targetElement.cloneNode(true) as HTMLElement
    clonedElement.querySelectorAll(".no-print").forEach((node) => node.remove())

    const wrapper = document.createElement("div")
    wrapper.appendChild(clonedElement)

    const safeHtml = sanitizePrintableHtml(wrapper.innerHTML)
    return await printViaHiddenIframe(safeHtml, documentTitle || fileName)
  } finally {
    onAfterExport?.(targetElement)
  }
}
